/**
 * MapNotifications
 * 
 * @atomic - Displays map notifications (location, loading, no results)
 * @idempotent - Pure component with React.memo
 * @modular - Self-contained notification display
 * 
 * Purpose: Shows contextual notifications above the map
 * Props: userLocation, loading state, entities, searchLocationName
 * Returns: Notification banners (location, loading, no results)
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDistanceUnit } from '../../hooks/useDistanceUnit'

const MapNotifications = memo(function MapNotifications({
  userLocation,
  searchLocationName,
  radius,
  isLoading,
  entities,
  children // For MapLegend or other custom content
}) {
  const { t } = useTranslation()
  const { convertFromKm, unit } = useDistanceUnit()

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Location Context */}
      {userLocation && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-2 sm:p-3">
          <span className="font-medium">{t('map.searchingAround')}</span>{' '}
          {searchLocationName || `${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`}
          <span className="ml-2 text-gray-500">
            • {t('map.withinRadius', { radius: convertFromKm(radius, 0), unit: t('unit.distanceAbbr') })}
          </span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white-50 border border-primary-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <p className="text-primary-800 text-sm font-medium">
              {t('map.loadingNearbyFoodTrucks')}
            </p>
          </div>
        </div>
      )}

      {/* No Results */}
      {!isLoading && userLocation && entities.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
          <p className="text-gray-700 text-sm">
            {t('map.noFoodTrucksFound', { radius: convertFromKm(radius, 0), unit })}
          </p>
        </div>
      )}

      {/* Custom content (e.g., MapLegend) */}
      {!isLoading && entities.length > 0 && children}
    </div>
  )
})

export default MapNotifications
